import { useState, useCallback } from 'react';
import { QueryKey } from 'react-query';

import {
  UniversalFilterCheckboxValue,
  UniversalFilterCheckboxFilterState,
  UniversalFilterCheckboxOnFilterStateChange,
  UniversalFilterCheckboxOnChange
} from '../../UniversalFilterCheckbox.types';

import { useMountEffect } from '../../../../../../../common/hooks/useMountEffect';
import { useReactQueryState } from '../../../../../../../main/common/reactQuery/hooks/useReactQueryState';

import { useUniversalFilterCheckboxFilterState } from '../useUniversalFilterCheckboxFilterState';

interface UniversalFilterCheckboxOptions {
  filterStateCacheKey: QueryKey;
  filterValue?: UniversalFilterCheckboxValue;
  onChange: UniversalFilterCheckboxOnChange;
}

function useUniversalFilterCheckbox({
  filterStateCacheKey,
  filterValue,
  onChange
}: UniversalFilterCheckboxOptions) {
  const { value: savedState, setValue: saveState } =
    useReactQueryState<UniversalFilterCheckboxFilterState>(filterStateCacheKey);

  const { handleApplyFilterState, handleParseFilterValue } =
    useUniversalFilterCheckboxFilterState({
      onChange
    });

  const [filterState, setFilterState] =
    useState<UniversalFilterCheckboxFilterState>(
      savedState ||
        handleParseFilterValue(filterValue) || {
          value: false
        }
    );

  useMountEffect(() => {
    handleApplyFilterState(filterState);
  });

  const handleFilterStateChange =
    useCallback<UniversalFilterCheckboxOnFilterStateChange>(
      (state) => {
        setFilterState(state);
        handleApplyFilterState(state);
        saveState(state);
      },
      [handleApplyFilterState, saveState]
    );

  const handleCheckboxInputChange = useCallback<(value: boolean) => void>(
    (value) => {
      handleFilterStateChange({ value });
    },
    [handleFilterStateChange]
  );

  return {
    filterState,
    handleCheckboxInputChange
  };
}

export default useUniversalFilterCheckbox;
